import { ReactNode } from 'react';

interface Props {
  icon: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  title?: string;

  variant?:
    | 'primary'
    | 'secondary'
    | 'success'
    | 'warning'
    | 'danger'
    | 'dark'
    | 'info';

  size?: 'sm' | 'md' | 'lg';

  className?: string;
}

const IconButton = ({
  icon,
  onClick,
  disabled = false,
  title,
  variant = 'secondary',
  size = 'md',
  className = '',
}: Props) => {
  const variantClasses = {
    primary: `
      border border-primary
      bg-primary
      text-white
      hover:opacity-90
    `,

    secondary: `
      border border-stroke
      bg-white
      text-bodydark2

      hover:border-primary
      hover:bg-primary/5
      hover:text-primary

      dark:border-strokedark
      dark:bg-boxdark-2
      dark:hover:border-primary
      dark:hover:bg-primary/10
      dark:hover:text-primary
    `,

    success: `
      border border-success/20
      bg-success/10
      text-success

      hover:bg-success
      hover:text-white
    `,

    warning: `
      border border-warning/20
      bg-warning/10
      text-warning

      hover:bg-warning
      hover:text-black
    `,

    danger: `
      border border-danger/20
      bg-danger/10
      text-danger

      hover:bg-danger
      hover:text-white
    `,

    dark: `
      border border-black
      bg-black
      text-white

      dark:bg-meta-4
    `,

    info: `
      border border-meta-5/20
      bg-meta-5/10
      text-meta-5

      hover:bg-meta-5
      hover:text-white
    `,
  };

  const sizeClasses = {
    sm: 'h-8 w-8',
    md: 'h-10.5 w-10.5',
    lg: 'h-12 w-12',
  };

  return (
    <button
      type="button"
      title={title}
      disabled={disabled}
      onClick={onClick}
      className={`
        inline-flex
        items-center
        justify-center

        rounded-xl

        transition-all
        duration-200

        active:scale-95

        focus:outline-none
        focus:ring-2
        focus:ring-primary/20

        ${sizeClasses[size]}

        ${
          disabled
            ? 'cursor-not-allowed opacity-50'
            : variantClasses[variant]
        }

        ${className}
      `}
    >
      {icon}
    </button>
  );
};

export default IconButton;